import { Decimal } from 'decimal.js'

import {
  Instrument,
  InstrumentKind,
  InstrumentType,
  TickSizeStep,
} from '@/types/generated/common/types'

/**
 * Client-side preview of the ladder the algo service will place. Mirrors
 * `plan_levels` in oems `services/algo/src/algos/scale/plan.rs` step for step, so
 * a ladder the preview accepts is one the server accepts at the same prices. The
 * server stays authoritative: it re-plans at start and refuses with its own reason.
 */

export const SCALE_MIN_ORDERS = 2
export const SCALE_MAX_ORDERS = 50
const SIZE_DP = 8

export type ScaleSide = 'buy' | 'sell'

export interface ScaleLadderSpec {
  tickSize: Decimal
  tickSizeSteps: TickSizeStep[]
  /** Lot step, only when quoted in the run's denomination (see `sizeStep`). */
  lotStep: Decimal | null
  /** Inverse perp/future: quantity is placed in quote. */
  nativeQuote: boolean
  minTradeAmount: Decimal | null
  minNotional: Decimal | null
}

export interface ScaleLadderInput {
  side: ScaleSide
  /** Total size, in `denomination`. */
  quantity: string
  quoteDenominated: boolean
  orderCount: number
  startPrice: string
  endPrice: string
  /** far ÷ near; 1 (or blank) is a flat ladder. */
  sizeRatio?: string
}

export interface ScaleLadderLevel {
  index: number
  price: Decimal
  quantity: Decimal
}

export type ScaleLadderError =
  | { kind: 'invalid'; field: 'quantity' | 'orderCount' | 'startPrice' | 'endPrice' | 'sizeRatio' }
  | { kind: 'equalBounds' }
  | { kind: 'noTick' }
  | { kind: 'nonPositiveLevel'; index: number }
  | { kind: 'duplicateLevel'; index: number; price: Decimal }
  | { kind: 'belowMinimum'; index: number; quantity: Decimal; minimum: Decimal }

export type ScaleLadderResult =
  | { ok: true; levels: ScaleLadderLevel[] }
  | { ok: false; error: ScaleLadderError }

function positive(value: string | undefined | null): Decimal | null {
  if (value == null) return null
  const trimmed = value.replace(/,/g, '').trim()
  if (!trimmed) return null
  try {
    const d = new Decimal(trimmed)
    return d.isFinite() && d.gt(0) ? d : null
  } catch {
    return null
  }
}

/** `quantity_is_quote_native`: an inverse contract that is not an option. */
function isQuoteNative(instrument: Pick<Instrument, 'kind' | 'type'>): boolean {
  return (
    instrument.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION &&
    instrument.type === InstrumentType.INSTRUMENT_TYPE_INVERSE
  )
}

/** `instrument_spec` + `size_step`: unparseable fields drop out rather than become zero. */
export function scaleLadderSpec(
  instrument: Pick<
    Instrument,
    | 'kind'
    | 'type'
    | 'tickSize'
    | 'tickSizeSteps'
    | 'qtyPrecision'
    | 'minTradeAmount'
    | 'minNotional'
  >,
  quoteDenominated: boolean
): ScaleLadderSpec {
  const nativeQuote = isQuoteNative(instrument)
  return {
    tickSize: positive(instrument.tickSize) ?? new Decimal(0),
    tickSizeSteps: instrument.tickSizeSteps ?? [],
    lotStep: quoteDenominated === nativeQuote ? positive(instrument.qtyPrecision) : null,
    nativeQuote,
    minTradeAmount: positive(instrument.minTradeAmount),
    minNotional:
      instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
        ? null
        : positive(instrument.minNotional),
  }
}

/** `effective_tick_size_at`: the step with the highest `abovePrice <= price`. */
export function effectiveTickSizeAt(steps: TickSizeStep[], minTick: Decimal, price: Decimal) {
  let best: { above: Decimal; tick: Decimal } | null = null
  for (const step of steps) {
    let above: Decimal
    let tick: Decimal
    try {
      above = new Decimal(step.abovePrice)
      tick = new Decimal(step.tickSize)
    } catch {
      continue
    }
    if (price.gte(above) && (best === null || above.gt(best.above))) best = { above, tick }
  }
  return best ? best.tick : minTick
}

/** `snap_away_from_touch`: a buy rounds down, a sell up. Null when it snaps to nothing. */
function snapAwayFromTouch(side: ScaleSide, price: Decimal, spec: ScaleLadderSpec) {
  if (price.lte(0)) return null
  const tick = effectiveTickSizeAt(spec.tickSizeSteps, spec.tickSize, price)
  if (tick.lte(0)) return null
  const ticks = price.dividedBy(tick)
  const snapped = (side === 'buy' ? ticks.floor() : ticks.ceil()).times(tick)
  return snapped.gt(0) ? snapped : null
}

function floorTo(value: Decimal, step: Decimal | null): Decimal {
  if (step && step.gt(0)) return value.dividedBy(step).floor().times(step)
  return value.toDecimalPlaces(SIZE_DP, Decimal.ROUND_DOWN)
}

/** `min_slice_in_denomination` at one level's price. */
function minimumAt(spec: ScaleLadderSpec, quoteDenominated: boolean, price: Decimal): Decimal {
  const floors: { floor: Decimal; quote: boolean }[] = []
  if (spec.minTradeAmount) floors.push({ floor: spec.minTradeAmount, quote: spec.nativeQuote })
  if (spec.minNotional) floors.push({ floor: spec.minNotional, quote: true })
  let max = new Decimal(0)
  for (const { floor, quote } of floors) {
    let converted: Decimal
    if (quote === quoteDenominated) converted = floor
    else if (quoteDenominated) converted = floor.times(price)
    else converted = floor.dividedBy(price)
    converted = converted.toDecimalPlaces(12, Decimal.ROUND_UP)
    if (converted.gt(max)) max = converted
  }
  return max
}

export function planScaleLadder(input: ScaleLadderInput, spec: ScaleLadderSpec): ScaleLadderResult {
  const fail = (error: ScaleLadderError): ScaleLadderResult => ({ ok: false, error })

  const n = input.orderCount
  if (!Number.isInteger(n) || n < SCALE_MIN_ORDERS || n > SCALE_MAX_ORDERS) {
    return fail({ kind: 'invalid', field: 'orderCount' })
  }
  const total = positive(input.quantity)
  if (!total) return fail({ kind: 'invalid', field: 'quantity' })
  const ratioRaw = input.sizeRatio?.trim()
  const ratio = ratioRaw ? positive(ratioRaw) : new Decimal(1)
  if (!ratio) return fail({ kind: 'invalid', field: 'sizeRatio' })
  const start = positive(input.startPrice)
  if (!start) return fail({ kind: 'invalid', field: 'startPrice' })
  const end = positive(input.endPrice)
  if (!end) return fail({ kind: 'invalid', field: 'endPrice' })

  const near = input.side === 'buy' ? Decimal.max(start, end) : Decimal.min(start, end)
  const far = input.side === 'buy' ? Decimal.min(start, end) : Decimal.max(start, end)
  if (near.eq(far)) return fail({ kind: 'equalBounds' })

  const span = far.minus(near)
  const last = n - 1
  const prices: Decimal[] = []
  for (let i = 0; i < n; i++) {
    const raw = near.plus(span.times(i).dividedBy(last))
    const price = snapAwayFromTouch(input.side, raw, spec)
    if (!price) {
      const tick = effectiveTickSizeAt(spec.tickSizeSteps, spec.tickSize, raw)
      return fail(tick.lte(0) ? { kind: 'noTick' } : { kind: 'nonPositiveLevel', index: i })
    }
    if (prices.some((p) => p.eq(price))) return fail({ kind: 'duplicateLevel', index: i, price })
    prices.push(price)
  }

  // Weights 1 + (r-1)·i/(n-1), scaled by n-1 so each is exact and a level's size takes
  // a single division — summing the unscaled ones at decimal.js's 20 digits mis-floors.
  const weights = Array.from({ length: n }, (_, i) => ratio.minus(1).times(i).plus(last))
  const weightSum = weights.reduce((acc, w) => acc.plus(w), new Decimal(0))
  const quantities: Decimal[] = []
  let allotted = new Decimal(0)
  for (const w of weights.slice(0, -1)) {
    const q = floorTo(total.times(w).dividedBy(weightSum), spec.lotStep)
    allotted = allotted.plus(q)
    quantities.push(q)
  }
  quantities.push(floorTo(total.minus(allotted), spec.lotStep))

  const levels: ScaleLadderLevel[] = []
  for (let i = 0; i < n; i++) {
    const quantity = quantities[i]
    const minimum = minimumAt(spec, input.quoteDenominated, prices[i])
    if (quantity.lte(0) || quantity.lt(minimum)) {
      return fail({ kind: 'belowMinimum', index: i, quantity, minimum })
    }
    levels.push({ index: i, price: prices[i], quantity })
  }
  return { ok: true, levels }
}
